import { PlatformLocation } from "@angular/common";
import { Component, ElementRef, HostBinding, inject, LOCALE_ID, OnInit, ViewChild } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { MenuItem } from "primeng/api";
import { AccountService } from "src/app/common/services/account.service";
import { AclService } from "src/app/common/services/acl.service";
import { ConfigService } from "src/app/common/services/config.service";
import { FloatingAssistantChatService } from "src/app/common/services/floating-assistant-chat.service";
import { LanguageService } from "src/app/common/services/language.service";
import { UserTrackingService } from "src/app/common/services/user-tracking.service";
import { config } from "src/app/config";
import { LayoutService } from "src/app/layout/service/app.layout.service";

@Component({
  selector: "app-topbar",
  templateUrl: "./app.topbar.component.html",
  styleUrls: ["./app.topbar.component.scss"],
})
export class AppTopBarComponent implements OnInit {
  private readonly aclService = inject(AclService);
  private readonly accountServive = inject(AccountService);
  readonly locale = inject(LOCALE_ID);
  private readonly floatingAssistantChatService = inject(FloatingAssistantChatService);
  private readonly userTrackingService = inject(UserTrackingService);
  items!: MenuItem[];
  @ViewChild("menubutton") menuButton!: ElementRef<HTMLButtonElement>;

  @ViewChild("topbarmenubutton") topbarMenuButton!: ElementRef<HTMLButtonElement>;

  @ViewChild("topbarmenu") menu!: ElementRef;

  canManageUsers = false;
  userBusinessUnit = "RTS";
  overrideSrfLogo = false;
  overrideRtsLogo = false;
  overrideRsiLogo = false;

  private readonly msalService = inject(MsalService);
  readonly account = this.msalService.instance.getAllAccounts()[0]!;

  readonly defaultProfileImage: string = "assets/img/avatar-default.png";

  profileImage: ArrayBuffer | string = this.defaultProfileImage;

  private readonly languageService = inject(LanguageService);

  private readonly configService = inject(ConfigService);
  readonly buildVersion = this.configService.buildVersion;
  protected readonly Number = Number;
  @HostBinding("class")
  environment = this.configService.environment;

  showTypeformLink = false;
  showTypeformDialog = false;
  protected typeformUrl = config.typeformUrl;
  protected typeformExpirationDate = config.typeformExpirationDate;

  showGuidelinePopUp = false;
  protected guidelinePopUpExpirationDate = config.guidelinePopUpExpirationDate;

  readonly frontendLanguages = this.languageService.getFrontendLanguages();
  readonly loc = inject(PlatformLocation);

  constructor(public layoutService: LayoutService) {}

  async ngOnInit(): Promise<void> {
    this.showTypeformLink = this.isPopUpNotExpired(this.typeformExpirationDate);
    this.showTypeformDialog =
      this.isPopUpNotExpired(this.typeformExpirationDate)
      && localStorage.getItem(`typeform_${this.typeformExpirationDate}_clicked`) !== "true";

    this.showGuidelinePopUp =
      !this.showTypeformDialog
      && this.isPopUpNotExpired(this.guidelinePopUpExpirationDate)
      && localStorage.getItem(`popup_guideline_${this.guidelinePopUpExpirationDate}_clicked`) !== "true";

    this.userBusinessUnit = await this.accountServive.getBusinessUnit();
    this.overrideSrfLogo = await this.aclService.isAuthorized("OVERRIDE_SRF_LOGO");
    this.overrideRtsLogo = await this.aclService.isAuthorized("OVERRIDE_RTS_LOGO");
    this.overrideRsiLogo = await this.aclService.isAuthorized("OVERRIDE_RSI_LOGO");
    this.canManageUsers = await this.aclService.isAuthorized("MANAGE_USERS");
    this.profileImage = this.defaultProfileImage;
  }

  get businessUnitLogo(): string {
    const rtsLogo = "assets/img/brand/rts_logo.png";
    const srfLogo = "assets/img/brand/srf_logo.png";
    const rsiLogo = "assets/img/brand/rsi_logo.png";
    if (this.overrideRtsLogo) {
      return rtsLogo;
    } else if (this.overrideSrfLogo) {
      return srfLogo;
    } else if (this.overrideRsiLogo) {
      return rsiLogo;
    }
    switch (this.userBusinessUnit) {
      case "RTS":
        return rtsLogo;
      case "SRF":
        return srfLogo;
      case "RSI":
        return rsiLogo;
      default:
        return rtsLogo;
    }
  }

  get prefixHref(): string {
    const href = this.loc.href;
    let index = href.indexOf("//") + 2;
    index = href.indexOf("/", index);
    return index < 0 ? href : href.substring(0, index);
  }

  logout(): void {
    this.msalService.logoutRedirect();
  }

  toggleChat(): void {
    this.floatingAssistantChatService.toggleVisibility();
  }

  async openAssistantForHelp(): Promise<void> {
    this.floatingAssistantChatService.openAssistantForHelp();
    await this.userTrackingService.track(this.account.username, "TOPBAR_ASSISTANT_HELP");
  }

  saveTypeformClick(): void {
    localStorage.setItem(`typeform_${this.typeformExpirationDate}_clicked`, "true");
    this.showTypeformDialog = false;
  }

  isPopUpNotExpired(date: string): boolean {
    return new Date() < new Date(date);
  }

  saveGuidelinePopUpClick(): void {
    localStorage.setItem(`popup_guideline_${this.guidelinePopUpExpirationDate}_clicked`, "true");
    this.showGuidelinePopUp = false;
  }
}
